/**
 * HLS composable (store-free)
 * Manages HLS.js instance, quality levels, audio tracks, and subtitles.
 * Receives a video element ref and returns reactive state.
 */

import { ref, shallowRef, computed, onUnmounted, type ComputedRef, type Ref } from 'vue'
import Hls, { type HlsConfig, type Level, type MediaPlaylist } from 'hls.js'
import type { QualityLevel, AudioTrack, SubtitleTrack } from '../types'
import { effectiveCap, hasEcoHeadroom as levelsHaveEcoHeadroom, shortSide, type PlayerBox } from '../utils/levelCaps'

export interface UseHLSOptions {
  videoRef: Ref<HTMLVideoElement | null>
  autoStartLoad?: boolean
  startLevel?: number
  debug?: boolean
  enableWorker?: boolean
  lowLatencyMode?: boolean
  backBufferLength?: number
  /**
   * Cap the forward buffer (seconds). Omit to keep hls.js defaults.
   *
   * hls.js computes its forward-buffer target as
   * `min(max(8 * maxBufferSize / bitrate, maxBufferLength), maxMaxBufferLength)`,
   * so on low-bitrate streams `maxBufferLength` alone is overridden by the
   * byte-based term — always set `maxMaxBufferLength` too for a hard cap.
   */
  maxBufferLength?: number
  /** Hard cap of the forward buffer (seconds) — see maxBufferLength. */
  maxMaxBufferLength?: number
  /** Cap the forward buffer (bytes). Omit to keep hls.js defaults. */
  maxBufferSize?: number
  deferredLoad?: boolean
  /** Start with eco mode enabled (auto-quality capped at 720p). */
  ecoModeDefault?: boolean
  /**
   * Hold auto quality at the smallest rung that fills the video element's box in device pixels
   * (`utils/levelCaps.ts`). On by default; a manual pick is never held.
   */
  capToPlayerSize?: boolean
  onError?: (error: string) => void
  onManifestParsed?: () => void
  onBufferingChange?: (isBuffering: boolean) => void
  /**
   * hls.js has dropped part of the buffer — on a seek outside the buffered window, or on a
   * quality switch.
   *
   * Nothing else says so: the `progress` media event fires while data is *downloaded*, so a
   * range that disappears leaves whatever read it last holding a range that no longer exists.
   */
  onBufferFlushed?: () => void
}

export interface UseHLSReturn {
  hls: Ref<Hls | null>
  isSupported: Ref<boolean>
  error: Ref<string | null>

  // Quality
  availableQualities: Ref<QualityLevel[]>
  currentQualityIndex: Ref<number>
  isAutoQuality: Ref<boolean>
  setQualityLevel: (index: number) => void

  // Eco mode
  isEcoMode: Ref<boolean>
  toggleEcoMode: () => void
  /** Some level is above 720p, so eco mode has something to hold back. */
  hasEcoHeadroom: Ref<boolean>

  // Audio tracks
  availableAudioTracks: Ref<AudioTrack[]>
  currentAudioTrackIndex: Ref<number>
  setAudioTrack: (index: number) => void
  hearingImpairedTrack: ComputedRef<AudioTrack | undefined>
  toggleHearingImpairedTrack: () => void

  // Subtitles
  availableSubtitleTracks: Ref<SubtitleTrack[]>
  currentSubtitleTrackIndex: Ref<number>
  setSubtitleTrack: (index: number) => void
  getSubtitleTrackUrl: (trackIndex: number) => string | null

  // Loading / buffering
  isLoading: Ref<boolean>
  isBuffering: Ref<boolean>

  // Methods
  initHLS: (manifestUrl: string) => void
  destroyHLS: () => void
  startLoad: (startPosition?: number) => void
}

function getNativeLanguageName(langCode: string): string {
  try {
    // Display the language in its own language (autonym), e.g. "Français", "English".
    const displayNames = new Intl.DisplayNames([langCode], { type: 'language' })
    const name = displayNames.of(langCode)
    if (name && name !== langCode) {
      return name.charAt(0).toUpperCase() + name.slice(1)
    }
  } catch {
    // Intl.DisplayNames not supported or invalid code
  }
  return langCode.toUpperCase()
}

// Legacy marker that used to be baked into the label. The "auto-generated"
// notion is now signalled via the manifest CHARACTERISTICS attribute, but we
// still strip the suffix here so stale labels never leak into the title.
function isLegacyAutoGeneratedLabel(trackName: string): boolean {
  return /auto[- ]generated|générés? automatiquement/i.test(trackName)
}

function isDefaultBackendLabel(trackName: string): boolean {
  return /^[A-Z]{2,3} Subtitles/i.test(trackName)
}

/** One entry of a media element's native `AudioTrackList`, which TypeScript's DOM library omits. */
interface NativeAudioTrack {
  enabled: boolean
  label: string
  language: string
}

interface NativeAudioTrackList extends EventTarget {
  readonly length: number
  [index: number]: NativeAudioTrack
}

/** The element's native audio tracks, where the browser exposes them (Safari does). */
function nativeAudioTracks(videoEl: HTMLVideoElement): NativeAudioTrackList | null {
  return (videoEl as HTMLVideoElement & { audioTracks?: NativeAudioTrackList }).audioTracks ?? null
}

export function useHLS(options: UseHLSOptions): UseHLSReturn {
  const {
    videoRef,
    autoStartLoad = true,
    startLevel = -1,
    debug = false,
    enableWorker = true,
    lowLatencyMode = false,
    backBufferLength = 30,
    maxBufferLength,
    maxMaxBufferLength,
    maxBufferSize,
    deferredLoad = false,
    ecoModeDefault = false,
    capToPlayerSize = true,
    onError: onErrorCallback,
    onManifestParsed,
    onBufferingChange: onBufferingChangeCallback,
    onBufferFlushed,
  } = options

  const hls = shallowRef<Hls | null>(null)
  const isSupported = ref(Hls.isSupported())
  const error = ref<string | null>(null)
  const isLoading = ref(false)
  const isBuffering = ref(false)

  // Quality levels
  const availableQualities = ref<QualityLevel[]>([])
  const currentQualityIndex = ref(-1)
  const isAutoQuality = ref(true)

  // Eco mode (cap quality at 720p). Seeded from the resolved player config
  // (preset / org default) and persisted across playlist item switches so the
  // default — or the viewer's choice — carries over to the next video.
  const isEcoMode = ref(ecoModeDefault)
  const hasEcoHeadroom = ref(false)

  // Reports every size change of the video element, fullscreen included, so the size cap follows it.
  let resizeObserver: ResizeObserver | null = null

  // Audio tracks
  const availableAudioTracks = ref<AudioTrack[]>([])
  const currentAudioTrackIndex = ref(0)

  // Under Safari's native HLS, the track the browser enabled on its own at load: the one it starts
  // on — the manifest's DEFAULT=YES rendition, unless an AUTOSELECT one matches the viewer's
  // languages. WebKit's AudioTrack carries no `default`, so `enabled` before anybody chose is the
  // only trace of it: it is pinned the first time a track is seen enabled, and a viewer's switch,
  // which enables another, does not move it.
  let nativeDefaultAudioTrack: NativeAudioTrack | null = null

  // What this composable listens to on the element and its track lists, removed on every reload and
  // at teardown: the element outlives a source, and each `initHLS` would otherwise stack one more
  // set of listeners reading the same lists.
  let removeListeners: (() => void)[] = []

  function listen(target: EventTarget, type: string, listener: () => void): void {
    target.addEventListener(type, listener)
    removeListeners.push(() => target.removeEventListener(type, listener))
  }

  function stopListening(): void {
    for (const remove of removeListeners) remove()
    removeListeners = []
  }

  // Subtitles
  const availableSubtitleTracks = ref<SubtitleTrack[]>([])
  const currentSubtitleTrackIndex = ref(-1)

  function mapHlsLevels(levels: Level[]): QualityLevel[] {
    const seen = new Set<number>()
    return levels
      .map((level, index) => ({
        index,
        height: level.height,
        width: level.width,
        bitrate: level.bitrate,
        // A rung is named by its short side: a portrait video's 720×1280 rung is its 720p.
        name: level.name || `${shortSide(level) || level.height}p`,
      }))
      .filter((level) => {
        if (seen.has(level.height)) return false
        seen.add(level.height)
        return true
      })
  }

  function mapHlsAudioTracks(tracks: MediaPlaylist[]): AudioTrack[] {
    return tracks.map((track) => {
      // hls.js exposes the CHARACTERISTICS attribute from EXT-X-MEDIA either as
      // a comma-separated string or (in newer versions) as an array. Handle both.
      const rawCharacteristics = (track as unknown as { characteristics?: string | string[] }).characteristics
      const characteristicsList: string[] = Array.isArray(rawCharacteristics)
        ? rawCharacteristics
        : typeof rawCharacteristics === 'string'
          ? rawCharacteristics.split(',').map((c) => c.trim())
          : []
      const isHearingImpaired = characteristicsList.some((c) => c.includes('describes-music-and-sound'))
      return {
        index: track.id,
        language: track.lang || '',
        name: track.name || track.lang || `Track ${track.id}`,
        isDefault: track.default || false,
        isHearingImpaired,
      }
    })
  }

  function buildSubtitleDisplayName(lang: string, originalName: string, trackId: number): string {
    if (!lang) return originalName || `Subtitle ${trackId}`

    // A custom, user-provided label takes precedence — but never the legacy
    // auto-generated marker or the default backend label, both of which we
    // render as the native language name instead.
    if (originalName && !isLegacyAutoGeneratedLabel(originalName) && !isDefaultBackendLabel(originalName)) {
      return originalName
    }

    return getNativeLanguageName(lang)
  }

  function mapHlsSubtitleTracks(tracks: MediaPlaylist[]): SubtitleTrack[] {
    return tracks.map((track) => {
      const rawCharacteristics = (track as unknown as { characteristics?: string | string[] }).characteristics
      const characteristicsList: string[] = Array.isArray(rawCharacteristics)
        ? rawCharacteristics
        : typeof rawCharacteristics === 'string'
          ? rawCharacteristics.split(',').map((c) => c.trim())
          : []
      return {
        index: track.id,
        language: track.lang || '',
        name: buildSubtitleDisplayName(track.lang || '', track.name || '', track.id),
        isDefault: track.default || false,
        isAutoGenerated: characteristicsList.includes('com.videas.auto-generated'),
      }
    })
  }

  function initHLS(manifestUrl: string): void {
    const videoEl = videoRef.value
    if (!videoEl) {
      error.value = 'Video element not found'
      return
    }

    destroyHLS()

    if (Hls.isSupported()) {
      const hlsConfig: Partial<HlsConfig> = {
        autoStartLoad: deferredLoad ? false : autoStartLoad,
        startLevel,
        debug,
        enableWorker,
        lowLatencyMode,
        backBufferLength,
        ...(maxBufferLength !== undefined ? { maxBufferLength } : {}),
        ...(maxMaxBufferLength !== undefined ? { maxMaxBufferLength } : {}),
        ...(maxBufferSize !== undefined ? { maxBufferSize } : {}),
        // hls.js's own size capping stays off: its controller rewrites `autoLevelCapping` every
        // second from the element's size alone, which would erase the eco cap, and it honours the
        // full device pixel ratio. `applyLevelCaps` computes both caps instead.
        capLevelToPlayerSize: false,
        xhrSetup: (xhr: XMLHttpRequest) => {
          xhr.withCredentials = false
        },
      }

      const hlsInstance = new Hls(hlsConfig)
      hls.value = hlsInstance

      // Bounded recovery: a fatal network error triggers a single reload chain,
      // not an unthrottled startLoad() loop. Reset once a fragment buffers.
      let networkErrorRetries = 0
      const MAX_NETWORK_ERROR_RETRIES = 3

      // Synchronous on purpose: hls.js starts loading right after this event, and the first
      // fragment it picks must already be under the cap.
      hlsInstance.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        onLevels(data.levels)
        isLoading.value = false
        onManifestParsed?.()
      })

      // A level hls.js drops (an unsupported codec, a failing variant) shifts every index above
      // it, the cap included.
      hlsInstance.on(Hls.Events.LEVELS_UPDATED, (_event, data) => {
        onLevels(data.levels)
      })

      hlsInstance.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
        currentQualityIndex.value = data.level
      })

      hlsInstance.on(Hls.Events.AUDIO_TRACKS_UPDATED, (_event, data) => {
        availableAudioTracks.value = mapHlsAudioTracks(data.audioTracks)
      })

      hlsInstance.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_event, data) => {
        currentAudioTrackIndex.value = data.id
      })

      hlsInstance.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (_event, data) => {
        availableSubtitleTracks.value = mapHlsSubtitleTracks(data.subtitleTracks)
      })

      hlsInstance.on(Hls.Events.SUBTITLE_TRACK_SWITCH, (_event, data) => {
        currentSubtitleTrackIndex.value = data.id
      })

      hlsInstance.on(Hls.Events.BUFFER_FLUSHED, () => {
        onBufferFlushed?.()
      })

      hlsInstance.on(Hls.Events.FRAG_BUFFERED, () => {
        networkErrorRetries = 0
        if (isBuffering.value) {
          isBuffering.value = false
          onBufferingChangeCallback?.(false)
        }
      })

      hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR || data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
          isBuffering.value = true
          onBufferingChangeCallback?.(true)
        }

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              error.value = 'Network error'
              onErrorCallback?.('Network error')
              // Bounded retry: never spin startLoad() forever on a persistent
              // failure — that turns one dead stream into a request storm.
              // Exponential backoff (1s, 2s, 4s) lets short outages recover;
              // destroy is deferred out of the hls.js event dispatch.
              if (networkErrorRetries < MAX_NETWORK_ERROR_RETRIES) {
                networkErrorRetries += 1
                setTimeout(() => hlsInstance.startLoad(), 1000 * 2 ** (networkErrorRetries - 1))
              } else {
                setTimeout(() => destroyHLS(), 0)
              }
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              error.value = 'Media error'
              onErrorCallback?.('Media error')
              hlsInstance.recoverMediaError()
              break
            default:
              error.value = 'Fatal error'
              onErrorCallback?.('Fatal error')
              destroyHLS()
              break
          }
        }
      })

      hlsInstance.on(Hls.Events.LEVEL_LOADING, () => {
        isLoading.value = true
      })

      hlsInstance.on(Hls.Events.LEVEL_LOADED, () => {
        isLoading.value = false
      })

      // Reset buffering on playback resume
      listen(videoEl, 'playing', () => {
        if (isBuffering.value) {
          isBuffering.value = false
          onBufferingChangeCallback?.(false)
        }
      })

      if (capToPlayerSize && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => applyLevelCaps())
        resizeObserver.observe(videoEl)
      }

      hlsInstance.loadSource(manifestUrl)
      hlsInstance.attachMedia(videoEl)
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS (Safari)
      videoEl.src = manifestUrl
      error.value = null

      // Safari exposes the manifest's audio renditions as the element's own AudioTrackList,
      // which hls.js is not there to read: without this an iPhone never offers a second language.
      const nativeAudio = nativeAudioTracks(videoEl)
      availableAudioTracks.value = []
      currentAudioTrackIndex.value = 0
      if (nativeAudio) {
        const readAudioTracks = () => {
          let enabled = -1
          for (let i = 0; i < nativeAudio.length; i++) {
            if (nativeAudio[i]?.enabled) {
              enabled = i
              break
            }
          }
          if (enabled !== -1) nativeDefaultAudioTrack ??= nativeAudio[enabled] ?? null
          const tracks: AudioTrack[] = []
          for (let i = 0; i < nativeAudio.length; i++) {
            const track = nativeAudio[i]
            if (!track) continue
            tracks.push({
              index: i,
              language: track.language || '',
              name: track.label || track.language || `Track ${i + 1}`,
              isDefault: track === nativeDefaultAudioTrack,
              // Safari surfaces no CHARACTERISTICS on an audio track.
              isHearingImpaired: false,
            })
          }
          availableAudioTracks.value = tracks
          currentAudioTrackIndex.value = Math.max(enabled, 0)
        }
        listen(nativeAudio, 'addtrack', readAudioTracks)
        listen(nativeAudio, 'removetrack', readAudioTracks)
        listen(nativeAudio, 'change', readAudioTracks)
        listen(videoEl, 'loadedmetadata', readAudioTracks)
      }

      // Extract subtitle tracks from Safari's native text track list
      listen(videoEl, 'loadedmetadata', () => {
        const textTracks = videoEl.textTracks
        const tracks: SubtitleTrack[] = []
        for (let i = 0; i < textTracks.length; i++) {
          const track = textTracks[i]
          if (track.kind === 'subtitles' || track.kind === 'captions') {
            tracks.push({
              index: i,
              language: track.language || '',
              name: buildSubtitleDisplayName(track.language || '', track.label || '', i),
              isDefault: track.mode === 'showing',
              // Safari's native TextTrack exposes no CHARACTERISTICS; fall back to
              // the legacy label marker (clean labels simply yield false).
              isAutoGenerated: isLegacyAutoGeneratedLabel(track.label || ''),
            })
          }
        }
        availableSubtitleTracks.value = tracks
      })
    } else {
      error.value = 'HLS is not supported in this browser'
    }
  }

  function destroyHLS(): void {
    resizeObserver?.disconnect()
    resizeObserver = null
    stopListening()
    if (hls.value) {
      hls.value.destroy()
      hls.value = null
      availableQualities.value = []
      hasEcoHeadroom.value = false
      currentQualityIndex.value = -1
      isAutoQuality.value = true
      // isEcoMode is intentionally NOT reset: it's a viewer/preset preference
      // that must survive an HLS teardown (playlist navigation re-inits HLS,
      // and MANIFEST_PARSED re-applies the 720p cap when eco is still active).
      availableSubtitleTracks.value = []
      currentSubtitleTrackIndex.value = -1
      error.value = null
    }
    // Read off hls.js or, under native HLS, off the element's own list: the next source fills them.
    nativeDefaultAudioTrack = null
    availableAudioTracks.value = []
    currentAudioTrackIndex.value = 0
  }

  /** A new level list: the menu, the eco control's reason to show, and the cap all derive from it. */
  function onLevels(levels: Level[]): void {
    availableQualities.value = mapHlsLevels(levels)
    hasEcoHeadroom.value = levelsHaveEcoHeadroom(levels)
    applyLevelCaps()
  }

  /** The video element's CSS box, or `null` when the size cap is off or there is no element. */
  function playerBox(): PlayerBox | null {
    const videoEl = videoRef.value
    if (!capToPlayerSize || !videoEl) return null
    const { width, height } = videoEl.getBoundingClientRect()
    return { width, height }
  }

  /**
   * Set `autoLevelCapping` to the lower of the size cap and, while eco mode is on, the eco cap.
   *
   * A cap only bounds the fragments hls.js has yet to choose, so a raised one would wait for the
   * buffer ahead to play out at the old rung. In auto mode, `nextLevel = -1` has it refetched now —
   * what hls.js's own capping does on entering fullscreen. A manual pick is left where it is.
   */
  function applyLevelCaps(): void {
    const instance = hls.value
    if (!instance || instance.levels.length === 0) return

    const cap = effectiveCap(instance.levels, {
      box: playerBox(),
      pixelRatio: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
      eco: isEcoMode.value,
    })
    if (cap === instance.autoLevelCapping) return

    const reachable = instance.maxAutoLevel
    instance.autoLevelCapping = cap
    if (instance.autoLevelEnabled && instance.maxAutoLevel > reachable) {
      instance.nextLevel = -1
    }
  }

  /** A manual pick plays whatever eco mode says; picking Auto (-1) plays under the caps again. */
  function setQualityLevel(index: number): void {
    if (hls.value) {
      hls.value.currentLevel = index
      isAutoQuality.value = index === -1
    }
  }

  function enableEcoMode(): void {
    isEcoMode.value = true
    const instance = hls.value
    if (!instance) return
    applyLevelCaps()
    // Eco mode bounds auto mode: turning it on from a manual pick means going back to auto, under it.
    if (!instance.autoLevelEnabled) instance.nextLevel = -1
    isAutoQuality.value = true
  }

  /** Turning eco off leaves the size cap, and leaves a manual pick alone. */
  function disableEcoMode(): void {
    isEcoMode.value = false
    applyLevelCaps()
  }

  function toggleEcoMode(): void {
    if (isEcoMode.value) disableEcoMode()
    else enableEcoMode()
  }

  function setAudioTrack(index: number): void {
    if (hls.value) {
      hls.value.audioTrack = index
      return
    }
    // Safari native HLS: enable exactly one track of the element's own list.
    const videoEl = videoRef.value
    const nativeAudio = videoEl ? nativeAudioTracks(videoEl) : null
    if (!nativeAudio || index < 0 || index >= nativeAudio.length) return
    for (let i = 0; i < nativeAudio.length; i++) {
      const track = nativeAudio[i]
      if (track) track.enabled = i === index
    }
    currentAudioTrackIndex.value = index
  }

  // Hearing-impaired alternate audio track (enhanced voices, reduced music).
  // Detected from the HLS manifest CHARACTERISTICS attribute set by the backend.
  const hearingImpairedTrack = computed<AudioTrack | undefined>(() =>
    availableAudioTracks.value.find((t) => t.isHearingImpaired),
  )

  // Remember the track the user was on before switching to hearing-impaired,
  // so we can restore it on toggle-off.
  let previousAudioTrackIndex: number | null = null

  function toggleHearingImpairedTrack(): void {
    const hiTrack = hearingImpairedTrack.value
    if (!hiTrack) return

    if (currentAudioTrackIndex.value === hiTrack.index) {
      // Toggle off: restore previous track, or fall back to the default track
      const fallback =
        previousAudioTrackIndex ??
        availableAudioTracks.value.find((t) => t.isDefault)?.index ??
        availableAudioTracks.value.find((t) => !t.isHearingImpaired)?.index ??
        0
      setAudioTrack(fallback)
      previousAudioTrackIndex = null
    } else {
      previousAudioTrackIndex = currentAudioTrackIndex.value
      setAudioTrack(hiTrack.index)
    }
  }

  function setSubtitleTrack(index: number): void {
    if (hls.value) {
      hls.value.subtitleTrack = index
    } else {
      // Safari native HLS: toggle text track modes directly
      const videoEl = videoRef.value
      if (videoEl) {
        const textTracks = videoEl.textTracks
        for (let i = 0; i < textTracks.length; i++) {
          const track = textTracks[i]
          if (track.kind === 'subtitles' || track.kind === 'captions') {
            track.mode = i === index ? 'showing' : 'hidden'
          }
        }
        currentSubtitleTrackIndex.value = index
      }
    }
  }

  function getSubtitleTrackUrl(trackIndex: number): string | null {
    if (!hls.value) return null
    const track = hls.value.subtitleTracks.find((t) => t.id === trackIndex)
    if (!track) return null
    return track.url || null
  }

  function startLoad(startPosition?: number): void {
    if (hls.value) {
      hls.value.startLoad(startPosition ?? -1)
    }
  }

  onUnmounted(() => {
    destroyHLS()
  })

  return {
    hls,
    isSupported,
    error,
    availableQualities,
    currentQualityIndex,
    isAutoQuality,
    setQualityLevel,
    isEcoMode,
    toggleEcoMode,
    hasEcoHeadroom,
    availableAudioTracks,
    currentAudioTrackIndex,
    setAudioTrack,
    hearingImpairedTrack,
    toggleHearingImpairedTrack,
    availableSubtitleTracks,
    currentSubtitleTrackIndex,
    setSubtitleTrack,
    getSubtitleTrackUrl,
    isLoading,
    isBuffering,
    initHLS,
    destroyHLS,
    startLoad,
  }
}
